import { useState, useRef, useEffect } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import './PersonImageEditor.css';

const PersonImageEditor = ({ imageUrl, onSave, onCancel, isSaving = false }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [rotation, setRotation] = useState(0);
  const [isDraggingCrop, setIsDraggingCrop] = useState(false);
  const [isResizing, setIsResizing] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [canvasSize, setCanvasSize] = useState({ width: 500, height: 600 });

  // Use refs to track interaction state in event listeners
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(null);
  const isRotatingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const cropRef = useRef(crop);
  const rotationRef = useRef(rotation);
  const scaleRef = useRef(scale);
  const imageRef = useRef(null);
  const canvasSizeRef = useRef(canvasSize);

  // Keep refs in sync
  useEffect(() => { cropRef.current = crop; }, [crop]);
  useEffect(() => { rotationRef.current = rotation; }, [rotation]);
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { imageRef.current = image; }, [image]);
  useEffect(() => { canvasSizeRef.current = canvasSize; }, [canvasSize]);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImage(img);

      // Calculate initial scale to fit image in canvas
      const containerWidth = containerRef.current?.clientWidth || 500;
      const maxCanvasWidth = Math.min(containerWidth - 40, 500);
      const maxCanvasHeight = 450; // Reduced to fit better in modal

      const scaleToFit = Math.min(
        maxCanvasWidth / img.width,
        maxCanvasHeight / img.height,
        1
      );

      setScale(scaleToFit);
      setCanvasSize({
        width: Math.min(img.width * scaleToFit, maxCanvasWidth),
        height: Math.min(img.height * scaleToFit, maxCanvasHeight)
      });

      // Set initial crop to 80% of image, centered
      const cropWidth = img.width * 0.8;
      const cropHeight = img.height * 0.8;
      setCrop({
        x: (img.width - cropWidth) / 2,
        y: (img.height - cropHeight) / 2,
        width: cropWidth,
        height: cropHeight
      });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    if (image && canvasRef.current) {
      drawCanvas();
    }
  }, [image, crop, scale, canvasSize, rotation]);

  // Attach touch event listeners with { passive: false } for proper touch support
  // React's synthetic touch events are passive by default, which prevents preventDefault()
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouchStart = (e) => {
      if (!imageRef.current) return;
      e.preventDefault();
      e.stopPropagation();

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const touch = e.touches[0];
      const x = (touch.clientX - rect.left) * scaleX;
      const y = (touch.clientY - rect.top) * scaleY;

      // Get crop bounds using refs
      const img = imageRef.current;
      const sc = scaleRef.current;
      const cs = canvasSizeRef.current;
      const cr = cropRef.current;
      const rot = rotationRef.current;

      const imgWidth = img.width * sc;
      const imgHeight = img.height * sc;
      const offsetX = (cs.width - imgWidth) / 2;
      const offsetY = (cs.height - imgHeight) / 2;

      const bounds = {
        x: offsetX + cr.x * sc,
        y: offsetY + cr.y * sc,
        width: cr.width * sc,
        height: cr.height * sc
      };

      const centerBX = bounds.x + bounds.width / 2;
      const centerBY = bounds.y + bounds.height / 2;

      // Rotate point helper inline
      const rad = (-rot * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const invX = cos * (x - centerBX) - sin * (y - centerBY) + centerBX;
      const invY = sin * (x - centerBX) + cos * (y - centerBY) + centerBY;

      const handleSize = 12;
      const tolerance = 15; // Larger tolerance for touch

      // Check rotation handle
      const rotateHandleY = bounds.y - 30;
      const distToRotate = Math.sqrt(Math.pow(invX - centerBX, 2) + Math.pow(invY - rotateHandleY, 2));

      if (distToRotate < 18) {
        isRotatingRef.current = true;
        setIsRotating(true);
        dragStartRef.current = { x, y };
        setDragStart({ x, y });
        return;
      }

      // Check resize handles
      const handles = {
        topLeft: { x: bounds.x, y: bounds.y },
        topRight: { x: bounds.x + bounds.width, y: bounds.y },
        bottomLeft: { x: bounds.x, y: bounds.y + bounds.height },
        bottomRight: { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
        top: { x: bounds.x + bounds.width / 2, y: bounds.y },
        bottom: { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height },
        left: { x: bounds.x, y: bounds.y + bounds.height / 2 },
        right: { x: bounds.x + bounds.width, y: bounds.y + bounds.height / 2 }
      };

      for (const [name, pos] of Object.entries(handles)) {
        if (Math.abs(invX - pos.x) < handleSize + tolerance &&
          Math.abs(invY - pos.y) < handleSize + tolerance) {
          isResizingRef.current = name;
          setIsResizing(name);
          dragStartRef.current = { x, y };
          setDragStart({ x, y });
          return;
        }
      }

      // Check if inside crop area
      if (invX >= bounds.x && invX <= bounds.x + bounds.width &&
        invY >= bounds.y && invY <= bounds.y + bounds.height) {
        isDraggingRef.current = true;
        setIsDraggingCrop(true);
        dragStartRef.current = { x, y };
        setDragStart({ x, y });
      }
    };

    const handleTouchMove = (e) => {
      if (!imageRef.current) return;

      const isInteracting = isDraggingRef.current || isResizingRef.current || isRotatingRef.current;
      if (!isInteracting) return;

      e.preventDefault();
      e.stopPropagation();

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      const touch = e.touches[0];
      const x = (touch.clientX - rect.left) * scaleX;
      const y = (touch.clientY - rect.top) * scaleY;

      const img = imageRef.current;
      const sc = scaleRef.current;
      const cs = canvasSizeRef.current;
      const cr = cropRef.current;
      const rot = rotationRef.current;
      const ds = dragStartRef.current;

      const dx = (x - ds.x) / sc;
      const dy = (y - ds.y) / sc;

      if (isRotatingRef.current) {
        const imgWidth = img.width * sc;
        const imgHeight = img.height * sc;
        const offsetX = (cs.width - imgWidth) / 2;
        const offsetY = (cs.height - imgHeight) / 2;

        const centerX = offsetX + cr.x * sc + cr.width * sc / 2;
        const centerY = offsetY + cr.y * sc + cr.height * sc / 2;

        const angle = Math.atan2(y - centerY, x - centerX) * (180 / Math.PI);
        const startAngle = Math.atan2(ds.y - centerY, ds.x - centerX) * (180 / Math.PI);
        const deltaAngle = angle - startAngle;

        setRotation(prev => {
          const newRotation = prev + deltaAngle;

          const cropCenterX = cr.x + cr.width / 2;
          const cropCenterY = cr.y + cr.height / 2;

          const corners = [
            { x: cr.x, y: cr.y },
            { x: cr.x + cr.width, y: cr.y },
            { x: cr.x, y: cr.y + cr.height },
            { x: cr.x + cr.width, y: cr.y + cr.height }
          ];

          const rad = (newRotation * Math.PI) / 180;
          const cos = Math.cos(rad);
          const sin = Math.sin(rad);

          const allCornersInside = corners.every(corner => {
            const cdx = corner.x - cropCenterX;
            const cdy = corner.y - cropCenterY;
            const rotatedX = cropCenterX + cdx * cos - cdy * sin;
            const rotatedY = cropCenterY + cdx * sin + cdy * cos;
            return rotatedX >= 0 && rotatedX <= img.width &&
              rotatedY >= 0 && rotatedY <= img.height;
          });

          return allCornersInside ? newRotation : prev;
        });
        dragStartRef.current = { x, y };
        setDragStart({ x, y });
      } else if (isDraggingRef.current) {
        setCrop(prev => {
          let newX = prev.x + dx;
          let newY = prev.y + dy;

          newX = Math.max(0, Math.min(img.width - prev.width, newX));
          newY = Math.max(0, Math.min(img.height - prev.height, newY));

          if (rot !== 0) {
            const cropCenterX = newX + prev.width / 2;
            const cropCenterY = newY + prev.height / 2;

            const corners = [
              { x: newX, y: newY },
              { x: newX + prev.width, y: newY },
              { x: newX, y: newY + prev.height },
              { x: newX + prev.width, y: newY + prev.height }
            ];

            const rad = (rot * Math.PI) / 180;
            const cos = Math.cos(rad);
            const sin = Math.sin(rad);

            const allCornersInside = corners.every(corner => {
              const cdx = corner.x - cropCenterX;
              const cdy = corner.y - cropCenterY;
              const rotatedX = cropCenterX + cdx * cos - cdy * sin;
              const rotatedY = cropCenterY + cdx * sin + cdy * cos;
              return rotatedX >= 0 && rotatedX <= img.width &&
                rotatedY >= 0 && rotatedY <= img.height;
            });

            if (!allCornersInside) return prev;
          }

          return { ...prev, x: newX, y: newY };
        });
        dragStartRef.current = { x, y };
        setDragStart({ x, y });
      } else if (isResizingRef.current) {
        const resizeHandle = isResizingRef.current;
        setCrop(prev => {
          let newCrop = { ...prev };
          const aspectRatio = prev.width / prev.height;

          if (resizeHandle === 'topLeft' || resizeHandle === 'topRight' ||
            resizeHandle === 'bottomLeft' || resizeHandle === 'bottomRight') {

            if (resizeHandle === 'topLeft') {
              const newWidth = prev.width - dx;
              const newHeight = newWidth / aspectRatio;
              if (newWidth > 50 && newHeight > 50) {
                newCrop.x = prev.x + dx;
                newCrop.y = prev.y + (prev.height - newHeight);
                newCrop.width = newWidth;
                newCrop.height = newHeight;
              }
            } else if (resizeHandle === 'topRight') {
              const newWidth = prev.width + dx;
              const newHeight = newWidth / aspectRatio;
              if (newWidth > 50 && newHeight > 50) {
                newCrop.y = prev.y + (prev.height - newHeight);
                newCrop.width = newWidth;
                newCrop.height = newHeight;
              }
            } else if (resizeHandle === 'bottomLeft') {
              const newWidth = prev.width - dx;
              const newHeight = newWidth / aspectRatio;
              if (newWidth > 50 && newHeight > 50) {
                newCrop.x = prev.x + dx;
                newCrop.width = newWidth;
                newCrop.height = newHeight;
              }
            } else if (resizeHandle === 'bottomRight') {
              const newWidth = prev.width + dx;
              const newHeight = newWidth / aspectRatio;
              if (newWidth > 50 && newHeight > 50) {
                newCrop.width = newWidth;
                newCrop.height = newHeight;
              }
            }
          } else {
            if (resizeHandle.includes('top')) {
              const newY = Math.max(0, prev.y + dy);
              const newHeight = prev.height + (prev.y - newY);
              if (newHeight > 50) {
                newCrop.y = newY;
                newCrop.height = newHeight;
              }
            }
            if (resizeHandle.includes('bottom')) {
              newCrop.height = Math.max(50, Math.min(img.height - prev.y, prev.height + dy));
            }
            if (resizeHandle.includes('left')) {
              const newX = Math.max(0, prev.x + dx);
              const newWidth = prev.width + (prev.x - newX);
              if (newWidth > 50) {
                newCrop.x = newX;
                newCrop.width = newWidth;
              }
            }
            if (resizeHandle.includes('right')) {
              newCrop.width = Math.max(50, Math.min(img.width - prev.x, prev.width + dx));
            }
          }

          if (rot !== 0) {
            const cropCenterX = newCrop.x + newCrop.width / 2;
            const cropCenterY = newCrop.y + newCrop.height / 2;

            const corners = [
              { x: newCrop.x, y: newCrop.y },
              { x: newCrop.x + newCrop.width, y: newCrop.y },
              { x: newCrop.x, y: newCrop.y + newCrop.height },
              { x: newCrop.x + newCrop.width, y: newCrop.y + newCrop.height }
            ];

            const rad = (rot * Math.PI) / 180;
            const cos = Math.cos(rad);
            const sin = Math.sin(rad);

            const allCornersInside = corners.every(corner => {
              const cdx = corner.x - cropCenterX;
              const cdy = corner.y - cropCenterY;
              const rotatedX = cropCenterX + cdx * cos - cdy * sin;
              const rotatedY = cropCenterY + cdx * sin + cdy * cos;
              return rotatedX >= 0 && rotatedX <= img.width &&
                rotatedY >= 0 && rotatedY <= img.height;
            });

            if (!allCornersInside) return prev;
          }

          return newCrop;
        });
        dragStartRef.current = { x, y };
        setDragStart({ x, y });
      }
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      isDraggingRef.current = false;
      isResizingRef.current = null;
      isRotatingRef.current = false;
      setIsDraggingCrop(false);
      setIsResizing(null);
      setIsRotating(false);
    };

    // Add event listeners with { passive: false } to allow preventDefault()
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [image]); // Re-attach when image changes

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw full image (no rotation)
    const imgWidth = image.width * scale;
    const imgHeight = image.height * scale;
    const offsetX = (canvas.width - imgWidth) / 2;
    const offsetY = (canvas.height - imgHeight) / 2;

    ctx.drawImage(image, offsetX, offsetY, imgWidth, imgHeight);

    // Calculate crop area in canvas coordinates
    const cropX = offsetX + crop.x * scale;
    const cropY = offsetY + crop.y * scale;
    const cropW = crop.width * scale;
    const cropH = crop.height * scale;
    const centerX = cropX + cropW / 2;
    const centerY = cropY + cropH / 2;

    // Draw dark overlay with hole for crop area
    ctx.save();

    // Create path for the entire canvas
    ctx.beginPath();
    ctx.rect(0, 0, canvas.width, canvas.height);

    // Create path for rotated crop area (subtract from overlay)
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.rect(-cropW / 2, -cropH / 2, cropW, cropH);
    ctx.restore();

    // Fill with dark overlay using even-odd rule
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fill('evenodd');

    // Draw rotated crop border and handles
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);

    // Draw crop border
    ctx.strokeStyle = '#00b5ad';
    ctx.lineWidth = 3;
    ctx.strokeRect(-cropW / 2, -cropH / 2, cropW, cropH);

    // Draw resize handles
    const handleSize = 12;
    ctx.fillStyle = '#00b5ad';
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;

    const handles = [
      [-cropW / 2, -cropH / 2], // top-left
      [cropW / 2, -cropH / 2], // top-right
      [-cropW / 2, cropH / 2], // bottom-left
      [cropW / 2, cropH / 2], // bottom-right
      [0, -cropH / 2], // top
      [0, cropH / 2], // bottom
      [-cropW / 2, 0], // left
      [cropW / 2, 0] // right
    ];

    handles.forEach(([x, y]) => {
      ctx.fillRect(x - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
      ctx.strokeRect(x - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
    });

    // Draw rotation handle (above top center)
    const rotateHandleY = -cropH / 2 - 30;
    ctx.beginPath();
    ctx.arc(0, rotateHandleY, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Draw line connecting rotation handle to crop box
    ctx.beginPath();
    ctx.moveTo(0, rotateHandleY + 8);
    ctx.lineTo(0, -cropH / 2);
    ctx.strokeStyle = '#00b5ad';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.restore();
  };

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Handle both mouse and touch events
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const getCropBounds = () => {
    const imgWidth = image.width * scale;
    const imgHeight = image.height * scale;
    const offsetX = (canvasSize.width - imgWidth) / 2;
    const offsetY = (canvasSize.height - imgHeight) / 2;

    return {
      x: offsetX + crop.x * scale,
      y: offsetY + crop.y * scale,
      width: crop.width * scale,
      height: crop.height * scale
    };
  };

  const rotatePoint = (x, y, cx, cy, angle) => {
    const rad = (angle * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const nx = cos * (x - cx) - sin * (y - cy) + cx;
    const ny = sin * (x - cx) + cos * (y - cy) + cy;
    return { x: nx, y: ny };
  };

  const getResizeHandle = (mouseX, mouseY) => {
    const bounds = getCropBounds();
    const handleSize = 12;
    const tolerance = 8;
    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;

    // Transform mouse coordinates to rotated space
    const invRotated = rotatePoint(mouseX, mouseY, centerX, centerY, -rotation);

    const rotateHandleY = bounds.y - 30;

    // Check rotation handle first (in rotated space)
    const distToRotate = Math.sqrt(Math.pow(invRotated.x - centerX, 2) + Math.pow(invRotated.y - rotateHandleY, 2));
    if (distToRotate < 12) {
      return 'rotate';
    }

    const handles = {
      topLeft: { x: bounds.x, y: bounds.y },
      topRight: { x: bounds.x + bounds.width, y: bounds.y },
      bottomLeft: { x: bounds.x, y: bounds.y + bounds.height },
      bottomRight: { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
      top: { x: bounds.x + bounds.width / 2, y: bounds.y },
      bottom: { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height },
      left: { x: bounds.x, y: bounds.y + bounds.height / 2 },
      right: { x: bounds.x + bounds.width, y: bounds.y + bounds.height / 2 }
    };

    for (const [name, pos] of Object.entries(handles)) {
      if (Math.abs(invRotated.x - pos.x) < handleSize + tolerance &&
        Math.abs(invRotated.y - pos.y) < handleSize + tolerance) {
        return name;
      }
    }

    return null;
  };

  const isInsideCrop = (mouseX, mouseY) => {
    const bounds = getCropBounds();
    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;

    // Transform mouse coordinates to rotated space
    const invRotated = rotatePoint(mouseX, mouseY, centerX, centerY, -rotation);

    return invRotated.x >= bounds.x && invRotated.x <= bounds.x + bounds.width &&
      invRotated.y >= bounds.y && invRotated.y <= bounds.y + bounds.height;
  };

  const handleInteractionStart = (e) => {
    if (!image) return;

    // Prevent default to stop scrolling on touch devices
    if (e.type.startsWith('touch')) {
      e.preventDefault();
    }

    const { x, y } = getCanvasCoords(e);
    const handle = getResizeHandle(x, y);

    if (handle === 'rotate') {
      setIsRotating(true);
      setDragStart({ x, y });
    } else if (handle) {
      setIsResizing(handle);
      setDragStart({ x, y });
    } else if (isInsideCrop(x, y)) {
      setIsDraggingCrop(true);
      setDragStart({ x, y });
    }
  };

  const handleInteractionMove = (e) => {
    if (!image) return;

    // Prevent default to stop scrolling on touch devices when dragging
    if (e.type.startsWith('touch') && (isDraggingCrop || isResizing || isRotating)) {
      e.preventDefault();
    }

    const { x, y } = getCanvasCoords(e);
    const dx = (x - dragStart.x) / scale;
    const dy = (y - dragStart.y) / scale;

    if (isRotating) {
      const bounds = getCropBounds();
      const centerX = bounds.x + bounds.width / 2;
      const centerY = bounds.y + bounds.height / 2;

      const angle = Math.atan2(y - centerY, x - centerX) * (180 / Math.PI);
      const startAngle = Math.atan2(dragStart.y - centerY, dragStart.x - centerX) * (180 / Math.PI);
      const deltaAngle = angle - startAngle;

      setRotation(prev => {
        const newRotation = prev + deltaAngle;

        // Check if rotated crop corners stay within image bounds
        const imgWidth = image.width * scale;
        const imgHeight = image.height * scale;
        const offsetX = (canvasSize.width - imgWidth) / 2;
        const offsetY = (canvasSize.height - imgHeight) / 2;

        const cropCenterX = crop.x + crop.width / 2;
        const cropCenterY = crop.y + crop.height / 2;

        // Get the 4 corners of the crop box in image space
        const corners = [
          { x: crop.x, y: crop.y },
          { x: crop.x + crop.width, y: crop.y },
          { x: crop.x, y: crop.y + crop.height },
          { x: crop.x + crop.width, y: crop.y + crop.height }
        ];

        // Rotate corners and check if they're within image bounds
        const rad = (newRotation * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);

        const allCornersInside = corners.every(corner => {
          const dx = corner.x - cropCenterX;
          const dy = corner.y - cropCenterY;
          const rotatedX = cropCenterX + dx * cos - dy * sin;
          const rotatedY = cropCenterY + dx * sin + dy * cos;

          return rotatedX >= 0 && rotatedX <= image.width &&
            rotatedY >= 0 && rotatedY <= image.height;
        });

        return allCornersInside ? newRotation : prev;
      });
      setDragStart({ x, y });
    } else if (isDraggingCrop) {
      setCrop(prev => {
        // Calculate new position
        let newX = prev.x + dx;
        let newY = prev.y + dy;

        // Basic bounds check (without rotation)
        newX = Math.max(0, Math.min(image.width - prev.width, newX));
        newY = Math.max(0, Math.min(image.height - prev.height, newY));

        // If rotated, check that all corners stay within image bounds
        if (rotation !== 0) {
          const cropCenterX = newX + prev.width / 2;
          const cropCenterY = newY + prev.height / 2;

          const corners = [
            { x: newX, y: newY },
            { x: newX + prev.width, y: newY },
            { x: newX, y: newY + prev.height },
            { x: newX + prev.width, y: newY + prev.height }
          ];

          const rad = (rotation * Math.PI) / 180;
          const cos = Math.cos(rad);
          const sin = Math.sin(rad);

          const allCornersInside = corners.every(corner => {
            const cdx = corner.x - cropCenterX;
            const cdy = corner.y - cropCenterY;
            const rotatedX = cropCenterX + cdx * cos - cdy * sin;
            const rotatedY = cropCenterY + cdx * sin + cdy * cos;

            return rotatedX >= 0 && rotatedX <= image.width &&
              rotatedY >= 0 && rotatedY <= image.height;
          });

          // If corners would go outside, don't move
          if (!allCornersInside) {
            return prev;
          }
        }

        return { ...prev, x: newX, y: newY };
      });
      setDragStart({ x, y });
    } else if (isResizing) {
      setCrop(prev => {
        let newCrop = { ...prev };
        const aspectRatio = prev.width / prev.height;

        // Corner handles - maintain aspect ratio
        if (isResizing === 'topLeft' || isResizing === 'topRight' ||
          isResizing === 'bottomLeft' || isResizing === 'bottomRight') {

          if (isResizing === 'topLeft') {
            const newWidth = prev.width - dx;
            const newHeight = newWidth / aspectRatio;
            if (newWidth > 50 && newHeight > 50) {
              newCrop.x = prev.x + dx;
              newCrop.y = prev.y + (prev.height - newHeight);
              newCrop.width = newWidth;
              newCrop.height = newHeight;
            }
          } else if (isResizing === 'topRight') {
            const newWidth = prev.width + dx;
            const newHeight = newWidth / aspectRatio;
            if (newWidth > 50 && newHeight > 50) {
              newCrop.y = prev.y + (prev.height - newHeight);
              newCrop.width = newWidth;
              newCrop.height = newHeight;
            }
          } else if (isResizing === 'bottomLeft') {
            const newWidth = prev.width - dx;
            const newHeight = newWidth / aspectRatio;
            if (newWidth > 50 && newHeight > 50) {
              newCrop.x = prev.x + dx;
              newCrop.width = newWidth;
              newCrop.height = newHeight;
            }
          } else if (isResizing === 'bottomRight') {
            const newWidth = prev.width + dx;
            const newHeight = newWidth / aspectRatio;
            if (newWidth > 50 && newHeight > 50) {
              newCrop.width = newWidth;
              newCrop.height = newHeight;
            }
          }
        } else {
          // Edge handles - free resize
          if (isResizing.includes('top')) {
            const newY = Math.max(0, prev.y + dy);
            const newHeight = prev.height + (prev.y - newY);
            if (newHeight > 50) {
              newCrop.y = newY;
              newCrop.height = newHeight;
            }
          }
          if (isResizing.includes('bottom')) {
            newCrop.height = Math.max(50, Math.min(image.height - prev.y, prev.height + dy));
          }
          if (isResizing.includes('left')) {
            const newX = Math.max(0, prev.x + dx);
            const newWidth = prev.width + (prev.x - newX);
            if (newWidth > 50) {
              newCrop.x = newX;
              newCrop.width = newWidth;
            }
          }
          if (isResizing.includes('right')) {
            newCrop.width = Math.max(50, Math.min(image.width - prev.x, prev.width + dx));
          }
        }

        // If rotated, check that all corners stay within image bounds
        if (rotation !== 0) {
          const cropCenterX = newCrop.x + newCrop.width / 2;
          const cropCenterY = newCrop.y + newCrop.height / 2;

          const corners = [
            { x: newCrop.x, y: newCrop.y },
            { x: newCrop.x + newCrop.width, y: newCrop.y },
            { x: newCrop.x, y: newCrop.y + newCrop.height },
            { x: newCrop.x + newCrop.width, y: newCrop.y + newCrop.height }
          ];

          const rad = (rotation * Math.PI) / 180;
          const cos = Math.cos(rad);
          const sin = Math.sin(rad);

          const allCornersInside = corners.every(corner => {
            const cdx = corner.x - cropCenterX;
            const cdy = corner.y - cropCenterY;
            const rotatedX = cropCenterX + cdx * cos - cdy * sin;
            const rotatedY = cropCenterY + cdx * sin + cdy * cos;

            return rotatedX >= 0 && rotatedX <= image.width &&
              rotatedY >= 0 && rotatedY <= image.height;
          });

          // If corners would go outside, don't resize
          if (!allCornersInside) {
            return prev;
          }
        }

        return newCrop;
      });
      setDragStart({ x, y });
    } else {
      // Update cursor based on hover
      const handle = getResizeHandle(x, y);
      if (handle === 'rotate') {
        canvasRef.current.style.cursor = 'grab';
      } else if (handle) {
        const cursors = {
          topLeft: 'nwse-resize',
          topRight: 'nesw-resize',
          bottomLeft: 'nesw-resize',
          bottomRight: 'nwse-resize',
          top: 'ns-resize',
          bottom: 'ns-resize',
          left: 'ew-resize',
          right: 'ew-resize'
        };
        canvasRef.current.style.cursor = cursors[handle];
      } else if (isInsideCrop(x, y)) {
        canvasRef.current.style.cursor = 'move';
      } else {
        canvasRef.current.style.cursor = 'default';
      }
    }
  };

  const handleInteractionEnd = () => {
    setIsDraggingCrop(false);
    setIsResizing(null);
    setIsRotating(false);
  };

  const handleSave = async () => {
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = crop.width;
    outputCanvas.height = crop.height;
    const ctx = outputCanvas.getContext('2d');

    // Calculate the center of the crop area in the original image
    const cropCenterX = crop.x + crop.width / 2;
    const cropCenterY = crop.y + crop.height / 2;

    // Translate to center, rotate, then translate back
    ctx.translate(crop.width / 2, crop.height / 2);
    ctx.rotate((-rotation * Math.PI) / 180); // Negative to extract rotated area
    ctx.translate(-cropCenterX, -cropCenterY);

    // Draw the entire image, the rotation will extract the rotated area
    ctx.drawImage(image, 0, 0);

    outputCanvas.toBlob(async (blob) => {
      await onSave(blob);
    }, 'image/jpeg', 0.92);
  };

  return (
    <div className="person-image-editor" ref={containerRef}>
      {isSaving && (
        <div className="editor-loading-overlay">
          <div className="ui active loader large"></div>
        </div>
      )}

      <div className="editor-canvas-container">
        <canvas
          ref={canvasRef}
          onMouseDown={handleInteractionStart}
          onMouseMove={handleInteractionMove}
          onMouseUp={handleInteractionEnd}
          onMouseLeave={handleInteractionEnd}
          style={{ touchAction: 'none' }}
        />
      </div>

      <div className="editor-controls">
        <div className="action-buttons">
          <Button onClick={onCancel} disabled={isSaving}>Cancel</Button>
          <Button color="teal" onClick={handleSave} disabled={isSaving}>
            <Icon name="check" />
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PersonImageEditor;
